import React, { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"

import { IDLE, PENDING } from "const"
import { deepCopy, StateType } from "client/utils"
import {
    StoreType,
    LoadAllMailSettings,
    LoadAllMailTemplates,
    setMailSettingList,
    setMailSettingEdit,
    setMailTemplateList,
    addModal,
} from "client/store"
import {
    Container,
    PageContent,
    PageForm,
    Collapse,
    Loader,
    Link,
    FormGroup,
    Label,
    Input,
    Textarea,
    DivInput,
    CKEditor,
    Empty,
    Button,
} from "client/components"
import {
    SettingList,
    Setting,
    SettingValue,
    TemplateWrapper,
} from "./styled"

import EditSetting from "./editSetting"
import EditTemplate from "./editTemplate"

const MAIN_SETTINGS = "main"

const Mail = () => {
    const settingList = useSelector((state: StoreType) => state.mail.settingList)
    const reloadSettingList = useSelector((state: StoreType) => state.mail.reloadSettingList)

    const templateList = useSelector((state: StoreType) => state.mail.templateList)
    const reloadTemplateList = useSelector((state: StoreType) => state.mail.reloadTemplateList)

    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [opened, setOpened] = useState<string[]>([MAIN_SETTINGS])
    const [loadSettingState, setLoadSettingState] = useState<StateType>(IDLE)
    const [loadTemplateState, setLoadTemplateState] = useState<StateType>(IDLE)

    useEffect(() => {
        if (!reloadSettingList) return
        setLoadSettingState(PENDING)
        LoadAllMailSettings().then((res) => {
            dispatch(setMailSettingList(res))
            setLoadSettingState(IDLE)
        })
    }, [reloadSettingList])

    useEffect(() => {
        if (!reloadTemplateList) return
        setLoadTemplateState(PENDING)
        LoadAllMailTemplates().then((res) => {
            dispatch(setMailTemplateList(res))
            setLoadTemplateState(IDLE)
        })
    }, [reloadTemplateList])

    const onCollapse = (tab: string) => {
        const newOpened: string[] = deepCopy(opened)
        if (newOpened.includes(tab)) {
            setOpened(newOpened.filter((id) => id !== tab))
        } else {
            newOpened.push(tab)
            setOpened(newOpened)
        }
    }

    return (
        <Container>
            <PageContent>
                {
                    (
                        loadSettingState === PENDING
                        || loadTemplateState === PENDING
                    ) && <Loader />
                }

                <Collapse
                    show={opened.includes(MAIN_SETTINGS)}
                    title={lang.mainSettings}
                    onCollapse={() => onCollapse(MAIN_SETTINGS)}
                >
                    <SettingList>
                        {
                            settingList.map((setting) => (
                                <Setting key={setting.name}>
                                    <Link
                                        href="/#"
                                        onClick={() => {
                                            dispatch(setMailSettingEdit(setting))
                                        }}
                                    >
                                        {setting.name}
                                    </Link>
                                    <SettingValue>{setting.value}</SettingValue>
                                </Setting>
                            ))
                        }
                    </SettingList>
                </Collapse>

                <PageForm>
                    <p>Available shortcuts:</p>

                    <p>
                        {"{USER} - user's first name and last name"}
                        <br />
                        {"{EMAIL} - user's email"}
                        <br />
                        {"{COMPANY} - user's registered company name, also the name of created account"}
                        <br />
                        {"{CONFIRM_LINK} - link to confirm email or change password, generated automatically"}
                        <br />
                    </p>
                </PageForm>

                {
                    templateList.map((template) => {
                        const key = template.name
                        const show = opened.includes(key)
                        const title = `${template.name} ${lang.template}`
                        return (
                            <Collapse
                                key={key}
                                show={show}
                                title={title}
                                onCollapse={() => onCollapse(key)}
                            >
                                <TemplateWrapper>
                                    <FormGroup>
                                        <Label>{lang.name}</Label>
                                        <Input type="text" value={template.name} readOnly />
                                    </FormGroup>
                                    <FormGroup>
                                        <Label>{lang.description}</Label>
                                        {
                                            template.description.length
                                                ? <Textarea value={template.description} readOnly />
                                                : <DivInput widePadding><Empty /></DivInput>
                                        }
                                    </FormGroup>
                                    <FormGroup>
                                        <Label>{lang.subject}</Label>
                                        {
                                            template.subject.length
                                                ? <Input type="text" value={template.subject} readOnly />
                                                : <DivInput widePadding><Empty /></DivInput>
                                        }
                                    </FormGroup>
                                    <FormGroup>
                                        <Label>{lang.content}</Label>
                                        <CKEditor
                                            data={template.content}
                                            isReadOnly
                                        />
                                    </FormGroup>

                                    <Button
                                        variant="success"
                                        onClick={() => dispatch(addModal({
                                            key: "editTemplate",
                                            content: <EditTemplate templateName={template.name} />,
                                        }))}
                                    >
                                        {lang.edit}
                                    </Button>
                                </TemplateWrapper>
                            </Collapse>
                        )
                    })
                }
            </PageContent>

            <EditSetting />
        </Container>
    )
}
export default Mail
